import { useState } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { sbUrl } from "@/utils/sbDomain";
import { useCalEmbed, openCalModal } from "@/hooks/useCalEmbed";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import {
  Handshake,
  MessageSquare,
  ClipboardList,
  Download,
  FileUp,
  Shield,
  Check,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Users,
  Lock,
  FileText,
  CheckCircle2,
  Sparkles,
  Video,
  Zap,
  Clock3,
  BadgeCheck,
  Mail,
  CalendarDays,
  Loader2,
  UserPlus,
} from "lucide-react";

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left"
        data-testid={`sb-faq-${question.slice(0, 20).replace(/\s/g, '-')}`}
      >
        <span className="font-medium text-gray-900 pr-4">{question}</span>
        {open ? (
          <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <p className="text-gray-600 pb-4 text-sm leading-relaxed">{answer}</p>
      )}
    </div>
  );
}

export default function SteuerberaterPortal() {
  useCalEmbed();

  // Contact form state
  const [form, setForm] = useState({
    name: "",
    firm: "",
    email: "",
    phone: "",
    mandants_count: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // "success" | "error" | null
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    setSubmitError("");
    try {
      await axios.post(`${API}/contact/advisor`, {
        ...form,
        source: "steuerberater-portal",
      });
      setSubmitStatus("success");
      setForm({
        name: "",
        firm: "",
        email: "",
        phone: "",
        mandants_count: "",
        message: "",
      });
    } catch (err) {
      setSubmitStatus("error");
      setSubmitError(
        err?.response?.data?.detail ||
          "Das hat leider nicht funktioniert. Bitte versuche es erneut oder schreibe uns direkt an info@ivoliq.io."
      );
    } finally {
      setSubmitting(false);
    }
  };

  useSEO({
    title: "Steuerberater-Portal: Freelancer & Steuerberater digital vernetzt | ivoliq",
    description:
      "Das ivoliq Steuerberater-Portal verbindet Freelancer und Steuerberater: gemeinsame Belege, Kommentare, Perioden-Freigaben, Aufgaben, Dokumenten-Anfragen & DATEV-Export. DSGVO-konform in DE, AT & CH.",
    canonical: "https://ivoliq.io/steuerberater-portal",
  });

  const advisorFeatures = [
    {
      icon: MessageSquare,
      title: "Kommentare & Rückfragen",
      desc: "Dein Steuerberater kommentiert direkt an Rechnungen, Belegen und Perioden. Keine E-Mail-Pingpong mehr – alles dokumentiert und auffindbar.",
    },
    {
      icon: ClipboardList,
      title: "Aufgaben & Freigaben",
      desc: "Offene Punkte werden als Aufgaben zugewiesen. Perioden (Monate / Quartale) bekommen klare Status: Offen, in Prüfung, Rückfragen, Freigegeben.",
    },
    {
      icon: FileUp,
      title: "Dokumenten-Anfragen",
      desc: "Dein Steuerberater fordert fehlende Belege strukturiert an. Du lädst sie direkt in ivoliq hoch – Zuordnung erfolgt automatisch.",
    },
    {
      icon: Download,
      title: "DATEV & Daten-Export",
      desc: "Ein Klick genügt: Alle Belege als ZIP, Excel-Übersichten und DATEV-kompatible CSV. Genau das, was dein Steuerberater braucht.",
    },
    {
      icon: Shield,
      title: "DSGVO-konform",
      desc: "Deutsche/europäische Rechenzentren, verschlüsselte Übertragung und rollenbasierter Zugriff. Nur freigegebene Daten sind sichtbar.",
    },
    {
      icon: Clock3,
      title: "Aktivitäts-Feed in Echtzeit",
      desc: "Alle Aktionen – Kommentare, Uploads, Exports, Freigaben – werden gefiltert und chronologisch angezeigt. Voller Kontext jederzeit.",
    },
  ];

  const workflowSteps = [
    {
      n: 1,
      icon: Users,
      title: "Steuerberater einladen",
      desc: "Du lädst deinen Steuerberater per E-Mail ein. Nach Bestätigung hat er Zugriff auf genau die Daten, die du freigibst.",
    },
    {
      n: 2,
      icon: FileText,
      title: "Belege & Rechnungen teilen",
      desc: "Alle Rechnungen und Belege, inkl. KI-erkannter OCR-Daten, stehen direkt zur Prüfung bereit. Kein Export/Import mehr.",
    },
    {
      n: 3,
      icon: MessageSquare,
      title: "Rückfragen klären",
      desc: "Unklare Belege? Dein Steuerberater kommentiert direkt – du antwortest per Nachricht oder lädst den fehlenden Beleg nach.",
    },
    {
      n: 4,
      icon: BadgeCheck,
      title: "Periode freigeben",
      desc: 'Ist der Monat oder das Quartal vollständig, setzt dein Steuerberater die Periode auf „Freigegeben". Alles ist dokumentiert.',
    },
    {
      n: 5,
      icon: Download,
      title: "DATEV-Export erzeugen",
      desc: "Per Klick erstellt ivoliq einen sauberen DATEV-Export – Belege als ZIP, Excel-Übersicht, CSV. Direkt zum Download.",
    },
  ];

  const benefitsFreelancer = [
    "Keine E-Mail-Chaos mit deinem Steuerberater mehr",
    "Weniger Rückfragen, weil Belege strukturiert vorliegen",
    "Kürzere Jahresabschlüsse, weil Daten sauber sind",
    "DATEV-Export mit einem Klick (Pro-Feature)",
    "Volle Nachvollziehbarkeit – jede Aktion ist dokumentiert",
    "Du behältst die Kontrolle: Zugriff nur auf freigegebene Daten",
  ];

  const benefitsAdvisor = [
    "Direkter Zugriff auf vorsortierte Belege & Rechnungen",
    "OCR-erkannte Daten statt handschriftliche Excel-Listen",
    "Strukturierte Aufgaben & Dokumenten-Anfragen",
    'Perioden-Freigaben statt „E-Mail-Ping-Pong"',
    "DATEV-CSV & ZIP direkt aus dem System",
    "Gestaffeltes Onboarding: mehrere Mandanten parallel betreuen",
  ];

  const faqItems = [
    {
      q: "Was ist das Steuerberater-Portal von ivoliq?",
      a: "Das Steuerberater-Portal ist ein eigener Bereich in ivoliq, über den dein Steuerberater digital auf deine Buchhaltung zugreifen kann. Er sieht Rechnungen, Belege und Finanzauswertungen, kann kommentieren, Aufgaben zuweisen, Perioden freigeben und DATEV-Exporte erzeugen – du behältst jederzeit die Kontrolle.",
    },
    {
      q: "Ist das Portal für meinen Steuerberater kostenlos?",
      a: "Ja. Dein Steuerberater nutzt das Portal kostenlos, solange du als Freelancer einen aktiven Plan hast. Es fallen für ihn keine Gebühren an. Die Funktionen sind auf die typische Zusammenarbeit zwischen Freelancer und Steuerberater in DACH ausgelegt.",
    },
    {
      q: "Wie lade ich meinen Steuerberater ein?",
      a: 'In ivoliq gehst du in den Bereich „Steuerberater" und trägst die E-Mail-Adresse deines Steuerberaters ein. Er erhält eine Einladung und einen eigenen Login unter /steuerberater. Nach Bestätigung siehst du seinen Zugriff und kannst ihn jederzeit wieder entziehen.',
    },
    {
      q: "Welche Daten werden geteilt?",
      a: "Nur das, was für die Buchhaltung relevant ist: Rechnungen, Belege, Finanzübersichten und Exports. Persönliche Einstellungen, Kundenlisten oder Projekte bleiben privat. Der Zugriff ist rollenbasiert und dokumentiert.",
    },
    {
      q: "Ist die Zusammenarbeit DSGVO-konform?",
      a: "Ja. Alle Daten werden ausschließlich in deutschen bzw. europäischen Rechenzentren gehostet und verschlüsselt übertragen. Der Zugriff ist rollenbasiert – dein Steuerberater sieht nur, was du freigibst.",
    },
    {
      q: "Unterstützt das Portal den DATEV-Export?",
      a: "Ja. Im Pro-Plan erstellst du mit einem Klick einen DATEV-kompatiblen Export inkl. ZIP-Archiv der Belege, Excel-Übersicht und CSV-Datei. Dein Steuerberater importiert das direkt in DATEV.",
    },
    {
      q: "Kann ich mehreren Mitarbeitern meines Steuerberaters Zugriff geben?",
      a: "Aktuell ist das Portal auf einen Haupt-Zugang pro Steuerberater ausgelegt. Mehrere Mandanten parallel zu betreuen ist kein Problem – dein Steuerberater sieht alle Freelancer-Accounts, die ihn eingeladen haben, in einem Dashboard.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "ivoliq Steuerberater-Portal",
            provider: {
              "@type": "Organization",
              name: "ivoliq",
              url: "https://ivoliq.io",
            },
            areaServed: ["DE", "AT", "CH"],
            description:
              "Digitale Zusammenarbeit zwischen Freelancer und Steuerberater: gemeinsame Belege, Kommentare, Aufgaben, Perioden-Freigaben und DATEV-Export.",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "EUR",
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        }}
      />

      {/* SIMPLE HEADER */}
      <header className="border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-emerald-600">
            ivoliq
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/pricing" className="text-gray-600 hover:text-gray-900 text-sm">
              Preise
            </Link>
            <a href={sbUrl("/steuerberater")} className="text-gray-600 hover:text-gray-900 text-sm" data-testid="sb-portal-advisor-login">
              Login Steuerberater
            </a>
            <Link to="/login" className="text-gray-600 hover:text-gray-900 text-sm">
              Login Freelancer
            </Link>
            <Link to="/signup">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Kostenlos starten
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative bg-gradient-to-b from-white via-emerald-50/30 to-white pt-16 sm:pt-20 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1.5 rounded-full text-sm font-semibold">
                <Handshake className="h-4 w-4" />
                Neu: Steuerberater-Portal
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Freelancer &amp; Steuerberater.{" "}
                <span className="text-emerald-600">Digital vernetzt.</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                Schluss mit E-Mail-Anhängen, verlorenen Belegen und endlosen
                Rückfragen. Das ivoliq Steuerberater-Portal verbindet dich und
                deinen Steuerberater in{" "}
                <strong className="text-gray-900">einer sicheren Plattform</strong>{" "}
                – mit DATEV-Export, Perioden-Freigaben und strukturierter
                Zusammenarbeit.
              </p>
              <div className="flex flex-col md:flex-row flex-wrap gap-3 relative z-20">
                <Link
                  to="/steuerberater?mode=register"
                  data-testid="sb-portal-hero-register"
                  className="inline-block"
                >
                  <Button
                    size="lg"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-5 text-base shadow-lg w-full sm:w-auto"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Kostenlos als Kanzlei registrieren
                  </Button>
                </Link>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-6 py-5 text-base w-full sm:w-auto"
                  onClick={openCalModal}
                  data-testid="sb-portal-hero-demo"
                >
                  <Video className="h-4 w-4 mr-2" />
                  30-Min-Demo buchen
                </Button>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 pt-2 text-sm text-gray-600">
                <span className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-600" /> DATEV-Export
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-600" /> DSGVO-konform
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-600" /> Server in der EU
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-600" /> DE / AT / CH
                </span>
              </div>
            </div>

            {/* Visual: Freelancer ↔ Steuerberater */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 sm:p-8 relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Users className="h-7 w-7 text-emerald-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-700">
                      Freelancer
                    </span>
                  </div>
                  <div className="flex-1 px-4 text-center">
                    <div className="flex items-center justify-center gap-1 text-emerald-600 mb-1">
                      <ArrowRight className="h-4 w-4" />
                      <Handshake className="h-5 w-5" />
                      <ArrowRight className="h-4 w-4 rotate-180" />
                    </div>
                    <span className="text-[11px] text-gray-500 uppercase tracking-wider">
                      Sichere Verbindung
                    </span>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                      <BadgeCheck className="h-7 w-7 text-blue-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-700">
                      Steuerberater
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">
                        Rückfrage zur Rechnung #2025-042
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        Kannst du den Beleg zum Hotel nachreichen?
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="h-4 w-4 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">
                        Aufgabe: USt-Voranmeldung Q1
                      </p>
                      <p className="text-xs text-gray-500">
                        Fällig: 10.04.2026
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <BadgeCheck className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">
                        Januar 2026 freigegeben
                      </p>
                      <p className="text-xs text-gray-500">
                        Alle Belege geprüft & archiviert
                      </p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-3 flex items-center gap-3 border border-emerald-200">
                    <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Download className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">
                        DATEV-Export bereit
                      </p>
                      <p className="text-xs text-gray-500">
                        belege.zip · uebersicht.xlsx · datev.csv
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -z-10 -top-4 -right-4 w-full h-full bg-emerald-100 rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* WARUM */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Warum ein eigenes Steuerberater-Portal?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Weil E-Mail-Anhänge, WhatsApp-Fotos und Excel-Tabellen kein
              professioneller Workflow sind. Das kostet dich Zeit – und deinen
              Steuerberater Nerven.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-red-200 bg-red-50/50">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-red-900 mb-4">
                  ❌ Ohne Steuerberater-Portal
                </h3>
                <ul className="space-y-3 text-gray-700">
                  {[
                    "Belege per E-Mail, WhatsApp, Drive hin und her",
                    "Rückfragen verlieren sich in alten E-Mail-Threads",
                    "Jedes Quartal das gleiche Export-Import-Chaos",
                    "Unklar, was bereits geprüft oder freigegeben ist",
                    "Manuelle DATEV-Aufbereitung kostet Stunden",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2 border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-emerald-900 mb-4">
                  ✅ Mit ivoliq Steuerberater-Portal
                </h3>
                <ul className="space-y-3 text-gray-700">
                  {[
                    "Ein Ort für alle Belege, Rechnungen & Rückfragen",
                    "Kommentare direkt an der Rechnung – dokumentiert",
                    "Strukturierte Perioden mit klarem Status",
                    "DATEV-Export auf Knopfdruck",
                    "DSGVO-konform, Server in der EU",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Alles, was Zusammenarbeit braucht
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Sechs Kernfunktionen, die deinen Workflow mit deinem Steuerberater
              auf ein professionelles Level heben.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advisorFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <Card
                  key={i}
                  className="border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all"
                >
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {f.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {f.desc}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              So läuft die Zusammenarbeit ab
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Fünf Schritte von der Einladung bis zum fertigen DATEV-Export.
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-8 top-4 bottom-4 w-0.5 bg-gradient-to-b from-emerald-400 to-blue-400 hidden md:block"></div>
            {workflowSteps.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.n}
                  className="relative flex items-start gap-6 md:pl-20 mb-10 last:mb-0"
                >
                  <div className="absolute left-5 w-7 h-7 bg-emerald-600 rounded-full border-4 border-white shadow-lg hidden md:flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{s.n}</span>
                  </div>
                  <div className="flex-1 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="md:hidden w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                        {s.n}
                      </div>
                      <Icon className="h-5 w-5 text-emerald-600" />
                      <h3 className="text-lg font-bold text-gray-900">
                        {s.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS - TWO SIDES */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Vorteile für beide Seiten
            </h2>
            <p className="text-lg text-gray-600">
              Das Portal ist so aufgebaut, dass Freelancer UND Steuerberater
              nachweislich Zeit sparen.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-emerald-500 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Users className="h-6 w-6 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Für Freelancer
                  </h3>
                </div>
                <ul className="space-y-3">
                  {benefitsFreelancer.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700">
                      <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-500 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 bg-blue-100 rounded-xl flex items-center justify-center">
                    <BadgeCheck className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Für Steuerberater
                  </h3>
                </div>
                <ul className="space-y-3">
                  {benefitsAdvisor.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700">
                      <Check className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <a href={sbUrl("/steuerberater")}>
                    <Button
                      variant="outline"
                      className="w-full border-blue-600 text-blue-700 hover:bg-blue-50"
                      data-testid="sb-portal-benefits-advisor-cta"
                    >
                      Als Steuerberater einloggen
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-3 py-1.5 rounded-full text-sm font-medium mb-4">
              <Lock className="h-4 w-4" />
              Sicherheit &amp; Datenschutz
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Datenschutz von Anfang an gedacht
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Wir wissen, dass Steuerdaten besonders sensibel sind. Deshalb
              haben wir Sicherheit und Compliance von Anfang an eingebaut.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: Shield,
                title: "DSGVO-konform",
                desc: "Alle Prozesse sind nach DSGVO aufgesetzt. Klare Datenminimierung und dokumentierte Verarbeitung.",
              },
              {
                icon: Lock,
                title: "Server in der EU",
                desc: "Alle Daten werden ausschließlich in deutschen/europäischen Rechenzentren gehostet.",
              },
              {
                icon: Zap,
                title: "TLS verschlüsselt",
                desc: "Ende-zu-Ende verschlüsselte Verbindungen, verschlüsseltes Object Storage.",
              },
              {
                icon: CheckCircle2,
                title: "Rollenbasiert",
                desc: "Dein Steuerberater sieht nur, was du freigibst – und nichts darüber hinaus.",
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur"
                >
                  <Icon className="h-6 w-6 text-emerald-400 mb-3" />
                  <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT & DEMO BOOKING */}
      <section id="kontakt" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1.5 rounded-full text-sm font-semibold mb-4">
              <Handshake className="h-4 w-4" />
              Für Steuerberater &amp; Kanzleien
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Werden Sie ivoliq-Partner
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Sie betreuen Freelancer oder Solopreneure? Lernen Sie das SB-Portal
              in einer 30-Min-Demo kennen oder schreiben Sie uns direkt.
              Der Zugang ist für Sie kostenlos.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Termin buchen */}
            <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-white">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center mb-5">
                  <CalendarDays className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Live-Demo buchen
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  30 Minuten. Wir zeigen Ihnen das Dashboard, den
                  Mandanten-Workflow, DATEV-Export und beantworten alle Fragen –
                  unverbindlich.
                </p>
                <ul className="space-y-2 mb-6">
                  {[
                    "Live-Tour durch das Steuerberater-Portal",
                    "Mandanten-Onboarding-Prozess ansehen",
                    "DATEV-Export & Perioden-Freigaben live",
                    "Individuelle Fragen zur Kanzlei-Integration",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  size="lg"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={openCalModal}
                  data-testid="sb-portal-contact-demo-cta"
                >
                  <Video className="h-5 w-5 mr-2" />
                  Termin buchen
                </Button>
                <p className="text-xs text-gray-500 mt-3 text-center">
                  Kostenlos &amp; unverbindlich · via cal.com
                </p>
              </CardContent>
            </Card>

            {/* Kontaktformular */}
            <Card className="border-2 border-gray-200 shadow-sm">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-5">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Nachricht schreiben
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Sie haben eine konkrete Frage oder möchten mehrere Mandanten
                  onboarden? Schreiben Sie uns – wir melden uns innerhalb von 1
                  Werktag.
                </p>

                {submitStatus === "success" ? (
                  <div
                    className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center"
                    data-testid="sb-portal-contact-success"
                  >
                    <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-3" />
                    <h4 className="text-lg font-bold text-emerald-900 mb-1">
                      Vielen Dank!
                    </h4>
                    <p className="text-sm text-emerald-800">
                      Ihre Nachricht ist angekommen. Wir melden uns innerhalb
                      von 1 Werktag bei Ihnen.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="sb-portal-contact-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="sb-name" className="text-sm">
                          Ihr Name <span className="text-red-600">*</span>
                        </Label>
                        <Input
                          id="sb-name"
                          required
                          minLength={2}
                          maxLength={120}
                          value={form.name}
                          onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                          }
                          placeholder="Max Mustermann"
                          className="mt-1"
                          data-testid="sb-contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="sb-firm" className="text-sm">
                          Kanzlei
                        </Label>
                        <Input
                          id="sb-firm"
                          maxLength={200}
                          value={form.firm}
                          onChange={(e) =>
                            setForm({ ...form, firm: e.target.value })
                          }
                          placeholder="Kanzlei Mustermann"
                          className="mt-1"
                          data-testid="sb-contact-firm"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="sb-email" className="text-sm">
                          E-Mail <span className="text-red-600">*</span>
                        </Label>
                        <Input
                          id="sb-email"
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) =>
                            setForm({ ...form, email: e.target.value })
                          }
                          placeholder="max@kanzlei.de"
                          className="mt-1"
                          data-testid="sb-contact-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="sb-phone" className="text-sm">
                          Telefon (optional)
                        </Label>
                        <Input
                          id="sb-phone"
                          type="tel"
                          maxLength={60}
                          value={form.phone}
                          onChange={(e) =>
                            setForm({ ...form, phone: e.target.value })
                          }
                          placeholder="+49 30 1234567"
                          className="mt-1"
                          data-testid="sb-contact-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="sb-mandants" className="text-sm">
                        Anzahl Freelancer-Mandanten (optional)
                      </Label>
                      <select
                        id="sb-mandants"
                        value={form.mandants_count}
                        onChange={(e) =>
                          setForm({ ...form, mandants_count: e.target.value })
                        }
                        className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        data-testid="sb-contact-mandants"
                      >
                        <option value="">Bitte wählen …</option>
                        <option value="1-5">1–5</option>
                        <option value="6-20">6–20</option>
                        <option value="21-50">21–50</option>
                        <option value="50+">Mehr als 50</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="sb-message" className="text-sm">
                        Ihre Nachricht <span className="text-red-600">*</span>
                      </Label>
                      <Textarea
                        id="sb-message"
                        required
                        minLength={5}
                        maxLength={5000}
                        rows={4}
                        value={form.message}
                        onChange={(e) =>
                          setForm({ ...form, message: e.target.value })
                        }
                        placeholder="Wir betreuen Freelancer in DE und möchten mehr über das SB-Portal erfahren …"
                        className="mt-1"
                        data-testid="sb-contact-message"
                      />
                    </div>

                    {submitStatus === "error" && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800" data-testid="sb-portal-contact-error">
                        {submitError}
                      </div>
                    )}

                    <Button
                      type="submit"
                      size="lg"
                      disabled={submitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="sb-portal-contact-submit"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sende …
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4 mr-2" />
                          Nachricht senden
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 text-center">
                      Mit dem Absenden akzeptieren Sie unsere{" "}
                      <Link to="/privacy" className="underline">
                        Datenschutzerklärung
                      </Link>
                      .
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Pro hint below */}
          <div className="mt-10 text-center">
            <div className="inline-flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-full px-4 py-2">
              <Sparkles className="h-4 w-4 text-emerald-600" />
              Für Ihre Mandanten im Pro-Plan enthalten · Ihr Zugang ist
              kostenlos
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Häufige Fragen zum Steuerberater-Portal
            </h2>
          </div>
          <Card className="border border-gray-200">
            <CardContent className="p-4 sm:p-6">
              {faqItems.map((item, i) => (
                <FAQItem key={i} question={item.q} answer={item.a} />
              ))}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Bereit, ivoliq in Ihrer Kanzlei einzusetzen?
          </h2>
          <p className="text-lg text-emerald-50 mb-8">
            Direkt loslegen oder erst eine Live-Demo: Sie entscheiden. Beides
            ist kostenlos und unverbindlich.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/steuerberater?mode=register" data-testid="sb-portal-final-register">
              <Button
                size="lg"
                className="bg-white text-emerald-700 hover:bg-gray-100 px-10 py-7 text-base shadow-xl"
              >
                <UserPlus className="h-5 w-5 mr-2" />
                Kostenlos registrieren
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white/70 text-white hover:bg-white/10 px-8 py-7 text-base"
              onClick={openCalModal}
              data-testid="sb-portal-final-demo"
            >
              <CalendarDays className="h-5 w-5 mr-2" />
              30-Min-Demo
            </Button>
            <a href="#kontakt" data-testid="sb-portal-final-contact" className="hidden md:inline-block">
              <Button
                size="lg"
                variant="ghost"
                className="text-white hover:bg-white/10 px-6 py-7 text-base"
              >
                <Mail className="h-5 w-5 mr-2" />
                Anfrage
              </Button>
            </a>
          </div>
          <p className="text-sm text-emerald-100 mt-6">
            ✓ Sofort einsatzbereit • ✓ DSGVO-konform · Server in der EU • ✓ DATEV/BMD/DATEV-AT-Export integriert
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
